import { oc } from "@orpc/contract";
import { z } from "zod";
import { Cents } from "./money";

/**
 * Paying for an order through Pay.nl (CONTEXT.md, `payment`; spec, "Betalen"): public, for the
 * shop.
 *
 * - `start` starts a Pay.nl payment for an open order with the method the customer picked, and
 *   answers where to send them. A customer who comes back may start another one, with the same
 *   or another instant method, as long as the order is open, unpaid and not expired. Switching to
 *   a bank transfer from another method is refused (`methodNotAllowed`): that needs a new order.
 *   It takes the order's `paymentToken` from checkout (`PlacedOrder`): the order number alone is
 *   guessable. A wrong token answers as an unknown order (404).
 * - Pay.nl sends the customer back to the shop's `/bestelling/{orderNumber}?betaling={paymentId}`
 *   (paid or not); that page reads `get` to show how the payment stands.
 * - What decides the payment is Pay.nl's exchange call to the API (`/api/payments/exchange`, not
 *   in this contract: only Pay.nl calls it).
 *
 * Errors come back as `{ _tag, message }` with an HTTP status: `PaymentOrderNotFound` (404),
 * `PaymentNotAllowed` (409, with `reason`), `PaymentRefused` (502: Pay.nl said no),
 * `PaymentUnavailable` (503: Pay.nl could not be reached; try again), `PaymentNotFound` (404).
 */

export const PaymentMethod = z.enum(["ideal", "bancontact", "card", "paypal", "bankTransfer"]);
export type PaymentMethod = z.infer<typeof PaymentMethod>;

/**
 * - `open`: started, not finished (a bank transfer stays open until the money arrives);
 * - `authorized`: reserved on a card, not yet taken;
 * - `paid`; `cancelled`, `expired`, `failed`: final, the customer may start another payment;
 * - `partially_refunded`, `refunded`: paid, then (partly) refunded.
 */
export const PaymentStatus = z.enum([
	"open",
	"authorized",
	"paid",
	"cancelled",
	"expired",
	"failed",
	"partially_refunded",
	"refunded",
]);
export type PaymentStatus = z.infer<typeof PaymentStatus>;

/** Why an order cannot be paid (now). */
export const PaymentNotAllowedReason = z.enum([
	"paid",
	"expired",
	"cancelled",
	"nothingToPay",
	"methodNotAllowed",
]);
export type PaymentNotAllowedReason = z.infer<typeof PaymentNotAllowedReason>;

export const StartPaymentPath = z.object({ orderNumber: z.string().min(1).max(40) });
/** The proof that the caller placed the order: checkout's `paymentToken`. */
export const PaymentToken = z.string().min(1).max(512);

export const StartPaymentBody = z.object({ method: PaymentMethod, token: PaymentToken });
export type StartPaymentBody = z.infer<typeof StartPaymentBody>;

export const StartedPayment = z.object({
	paymentId: z.string(),
	/** Send the customer here to pay. `null` only if Pay.nl gave none (a bank transfer may not). */
	redirectUrl: z.string().nullable(),
	/** For a bank transfer: the reference to put on it. */
	transferReference: z.string().nullable(),
	amountCents: Cents,
	/** Started in Pay.nl's test mode: no real money (every Preview). */
	testMode: z.boolean(),
});
export type StartedPayment = z.infer<typeof StartedPayment>;

export const PaymentPath = z.object({ paymentId: z.string().min(1).max(64) });

export const Payment = z.object({
	paymentId: z.string(),
	method: PaymentMethod,
	status: PaymentStatus,
	amountCents: Cents,
	testMode: z.boolean(),
});
export type Payment = z.infer<typeof Payment>;

/**
 * The admin's Pay.nl check (`paynlCheck`): what this Worker sends Pay.nl and what Pay.nl says it
 * has. For finding out why Pay.nl refuses a payment (`PAY-2101`: the method is not on the service).
 *
 * - `requested`: each method the shop offers, Pay.nl's id for it, and whether that id is in
 *   Pay.nl's config of the service (`null`: the config could not be read).
 * - `answers`: Pay.nl's raw answers to the config request, secrets redacted.
 */
export const PaynlCheck = z.object({
	environment: z.string(),
	testMode: z.boolean(),
	configured: z.boolean(),
	serviceId: z.string().nullable(),
	tokenCode: z.string().nullable(),
	requested: z.array(z.object({ method: PaymentMethod, id: z.number(), onService: z.boolean().nullable() })),
	methodIdsOnService: z.array(z.number()).nullable(),
	answers: z.array(z.object({ url: z.string(), status: z.number().nullable(), body: z.unknown() })),
});
export type PaynlCheck = z.infer<typeof PaynlCheck>;

export const PaymentContract = {
	/** Admin: the Pay.nl check (`PaynlCheck`). Read-only; starts no payment. */
	paynlCheck: oc.route({ method: "GET", path: "/admin/payments/paynl-check" }).output(PaynlCheck),
	/** Start paying for an order. */
	start: oc
		.route({ method: "POST", path: "/shop/orders/{orderNumber}/payments" })
		.input(StartPaymentPath.extend(StartPaymentBody.shape))
		.output(StartedPayment),
	/**
	 * How a payment stands, for the shop's return page. While it is open the API asks Pay.nl first,
	 * so the page is right even when Pay.nl's exchange call has not arrived yet.
	 */
	get: oc.route({ method: "GET", path: "/shop/payments/{paymentId}" }).input(PaymentPath).output(Payment),
};
